import React, { useContext, useEffect, useRef, useState } from "react";
import { Button, Modal, Tag, Typography, Drawer, Steps, Space, Tooltip, Tabs, Collapse, Badge, Progress, Card, Input } from "antd";
import { BookOutlined, ExportOutlined, CheckCircleOutlined, CodeOutlined, LockOutlined, HistoryOutlined, SendOutlined, ArrowLeftOutlined, ReloadOutlined, FileAddOutlined } from "@ant-design/icons";
import Editor from "@monaco-editor/react";
import { PostCazaResposta } from "../../../../dao/jutge/PostCazaResposta";
import { AuthContext } from "../../../../config/AuthContext";
import { GetCazaHistoryAdmin } from "../../../../dao/jutge/GetCazaHistoryAdmin";
import UseWebSocketSubscription from "../../../UseWebSocketSubscription";

const { Text, Title } = Typography;
const { Panel } = Collapse;

interface StepItem {
    paso: number;
    titulo: string;
    instrucciones: string;
    mustInclude?: string[];
    mustNotInclude?: string[];
}

// ---- Soporte multi-fichero ----
// Cuando hay más de un fichero, se envían todos concatenados en el campo `code`
// precedidos por una línea marcador "// ===== FILE: Nombre.java =====".
// El backend (JutgeService.parseCazaSourceFiles) los separa y compila juntos.
interface CodeFile {
    name: string;
    content: string;
}

const FILE_MARKER_RE = /^[ \t]*\/\/[ \t]*=+[ \t]*FILE:[ \t]*([\w./-]+?)[ \t]*=+[ \t]*$/gm;

const detectPublicClass = (content: string): string | null => {
    const m = content.match(/public\s+(?:final\s+|abstract\s+)?(?:class|interface|enum|record)\s+(\w+)/);
    return m ? m[1] : null;
};

const parseFiles = (raw: string, fallbackName: string): CodeFile[] => {
    const text = raw || "";
    const matches: { index: number; full: string; name: string }[] = [];
    const re = new RegExp(FILE_MARKER_RE.source, "gm");
    let mm: RegExpExecArray | null;
    while ((mm = re.exec(text)) !== null) {
        matches.push({ index: mm.index, full: mm[0], name: mm[1] });
    }
    if (matches.length === 0) {
        const cls = detectPublicClass(text);
        return [{ name: cls ? `${cls}.java` : fallbackName, content: text }];
    }
    return matches.map((m, i) => {
        const start = m.index + m.full.length;
        const end = i + 1 < matches.length ? matches[i + 1].index : text.length;
        const content = text.substring(start, end).replace(/^\r?\n/, "").replace(/\r?\n$/, "");
        let name = m.name;
        if (!name.endsWith(".java")) name += ".java";
        return { name, content };
    });
};

const serializeFiles = (files: CodeFile[]): string => {
    if (files.length === 1) return files[0].content;
    return files.map(f => `// ===== FILE: ${f.name} =====\n${f.content}`).join("\n");
};

interface EscenarioCaza {
    id: number;
    nombre: string;
    descripcion: string;
    dificultad: "Beginner" | "Intermediate" | "Expert";
    imagen: string;
    background?: string;
    nivel?: number;
    instrucciones: string;
    vidaJavamon: number;
    mustInclude?: string[] | string;
    mustNotInclude?: string[] | string;
    estat?: number;
    obtained?: number;
    intentos_global?: number;
    intentos_restantes?: number;
    url_teoria?: string;
    totalPasos?: number;
    pasos?: StepItem[];
    id_aula?: number;
    aula?: string;
}

interface Props {
    escenario: EscenarioCaza;
    isRetro?: boolean;
    onExit: () => void;
    idConjunt?: number | null;
}

const CazaModal: React.FC<Props> = ({ escenario, onExit, idConjunt }) => {
    const editorRef = useRef<any>(null);
    const monacoRef = useRef<any>(null);
    const { userId } = useContext(AuthContext);
    // Fallback: use the id_conjunt carried in the escenario if not passed explicitly
    const conjuntId: number | null = (idConjunt ?? (escenario as any).id_conjunt) ?? null;

    const codigoInicial = `public class Solucion${escenario.id} {
    public static void main(String[] args) {

    }
}`;

    const defaultFileName = `Solucion${escenario.id}.java`;
    const [files, setFiles] = useState<CodeFile[]>([{ name: defaultFileName, content: codigoInicial }]);
    const [activeFileIdx, setActiveFileIdx] = useState<number>(0);
    const activeFileIdxRef = useRef<number>(0);
    activeFileIdxRef.current = activeFileIdx;
    const activeFile = files[activeFileIdx] || files[0];
    // Código combinado que se envía al backend (y sobre el que se comprueban los requisitos)
    const code = React.useMemo(() => serializeFiles(files), [files]);
    const setCode = (raw: string) => {
        setFiles(parseFiles(raw, defaultFileName));
        setActiveFileIdx(0);
    };

    // Modal para crear / renombrar ficheros
    const [fileNameModal, setFileNameModal] = useState<{ mode: "add" | "rename"; idx?: number } | null>(null);
    const [fileNameInput, setFileNameInput] = useState<string>("");
    const [fileNameError, setFileNameError] = useState<string>("");

    const openAddFile = () => {
        setFileNameInput("");
        setFileNameError("");
        setFileNameModal({ mode: "add" });
    };

    const openRenameFile = (idx: number) => {
        setFileNameInput(files[idx]?.name || "");
        setFileNameError("");
        setFileNameModal({ mode: "rename", idx });
    };

    const confirmFileName = () => {
        let name = fileNameInput.trim();
        if (name.endsWith(".java")) name = name.slice(0, -5);
        if (!/^[A-Za-z_$][\w$]*$/.test(name)) {
            setFileNameError("Nombre no válido. Usa un identificador Java (p. ej. Persona).");
            return;
        }
        const fullName = `${name}.java`;
        const clash = files.some((f, i) => f.name === fullName && !(fileNameModal?.mode === "rename" && i === fileNameModal.idx));
        if (clash) {
            setFileNameError("Ya existe un fichero con ese nombre.");
            return;
        }
        if (fileNameModal?.mode === "add") {
            const newFiles = [...files, { name: fullName, content: `public class ${name} {\n\n}\n` }];
            setFiles(newFiles);
            setActiveFileIdx(newFiles.length - 1);
        } else if (fileNameModal?.mode === "rename" && fileNameModal.idx !== undefined) {
            const idx = fileNameModal.idx;
            setFiles(prev => prev.map((f, i) => i === idx ? { ...f, name: fullName } : f));
        }
        setFileNameModal(null);
    };

    const removeFile = (idx: number) => {
        if (files.length <= 1) return;
        Modal.confirm({
            title: `¿Eliminar ${files[idx].name}?`,
            content: "Se perderá el contenido de este fichero.",
            okText: "Eliminar",
            okButtonProps: { danger: true },
            cancelText: "Cancelar",
            onOk: () => {
                setFiles(prev => prev.filter((_, i) => i !== idx));
                setActiveFileIdx(prev => (prev >= idx ? Math.max(0, prev - 1) : prev));
            }
        });
    };
    const [theoryDrawerOpen, setTheoryDrawerOpen] = useState<boolean>(false);
    const [validatingStep, setValidatingStep] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<string>("instrucciones");
    const [history, setHistory] = useState<any[]>([]);
    const [loadingHistory, setLoadingHistory] = useState<boolean>(false);

    // Theory URLs resolution (support multiple URLs split by comma or newline)
    const theoryUrls: string[] = React.useMemo(() => {
        if (!escenario.url_teoria || !escenario.url_teoria.trim()) return [];
        const parsed = escenario.url_teoria
            .split(/[,\n]/)
            .map(u => u.trim())
            .filter(u => u.length > 0);
        return parsed;
    }, [escenario.url_teoria]);
    const [selectedTheoryIdx, setSelectedTheoryIdx] = useState<number>(0);
    const activeTheoryUrl = theoryUrls[selectedTheoryIdx] || theoryUrls[0] || "";

    // --- STEP-BY-STEP LEARNING LOGIC ---
    const parsedPasos: StepItem[] = React.useMemo(() => {
        if (escenario.pasos && escenario.pasos.length > 0) {
            return escenario.pasos;
        }
        const text = escenario.instrucciones || "";

        // 1. Try JSON array format
        try {
            const parsed = JSON.parse(text);
            if (Array.isArray(parsed) && parsed.length > 0) {
                return parsed.map((p: any, idx: number) => ({
                    paso: p.paso || idx + 1,
                    titulo: p.titulo || `Paso ${idx + 1}`,
                    instrucciones: p.instrucciones || ""
                }));
            }
        } catch (e) {
            // Not JSON
        }

        // 2. Fallback delimiter check
        const stepSplit = text.split(/(?=###\s*Paso|\bPASO\s+\d+:?)/i).filter(s => s.trim().length > 0);
        if (stepSplit.length > 1) {
            return stepSplit.map((s, idx) => {
                const lines = s.trim().split("\n");
                const title = lines[0].replace(/^[#\s*]+/, "").trim();
                const desc = lines.slice(1).join("\n").trim();
                return {
                    paso: idx + 1,
                    titulo: title || `Paso ${idx + 1}`,
                    instrucciones: desc || s.trim()
                };
            });
        }

        // 3. Fallback: single step
        if (text.trim().length > 0) {
            return [
                { paso: 1, titulo: "Paso 1: Implementación Principal", instrucciones: text }
            ];
        }

        return [
            { paso: 1, titulo: "Paso 1: Estructura y Variables", instrucciones: "Declara las variables necesarias y la estructura de la clase." },
            { paso: 2, titulo: "Paso 2: Lógica Principal", instrucciones: "Implementa la lógica del problema según las instrucciones de teoría." },
            { paso: 3, titulo: "Paso 3: Salida y Validación Final", instrucciones: "Verifica que la salida estándar coincide exactamente con el formato esperado." }
        ];
    }, [escenario.pasos, escenario.instrucciones]);

    const totalSteps = escenario.totalPasos || parsedPasos.length;
    
    // Step currently active for the student
    const [pasoActual, setPasoActual] = useState<number>(1);
    // Highest completed step
    const [maxPasoCompletado, setMaxPasoCompletado] = useState<number>(0);

    const initialCodeLoadedRef = useRef<boolean>(false);
    // Flag to suppress WebSocket-triggered refreshes while the user is actively polling
    const isPollingRef = useRef<boolean>(false);

    const fetchHistory = async (isInitialLoad = false, shouldAdvanceStep = isInitialLoad) => {
        if (!userId || !escenario.id) return;
        setLoadingHistory(true);
        try {
            const data = await GetCazaHistoryAdmin(escenario.id.toString(), userId, conjuntId);
            // Compute highest passed step from history
            if (Array.isArray(data) && data.length > 0) {
                // Ensure data is sorted from newest to oldest by id / moment
                const sortedData = [...data].sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0));
                setHistory(sortedData);

                let highest = 0;
                sortedData.forEach((att: any) => {
                    const isAttOk = att.estat === 1;
                    const stepNum = Number(att.ordre) || 1;
                    if (isAttOk && stepNum > highest) {
                        highest = stepNum;
                    }
                });
                setMaxPasoCompletado(highest);

                if (shouldAdvanceStep) {
                    // Next active step is highest passed + 1, capped at totalSteps
                    const nextStep = Math.min(totalSteps, highest + 1);
                    setPasoActual(nextStep);
                }

                // Always load the most recent submission's code on initial mount
                if (isInitialLoad || !initialCodeLoadedRef.current) {
                    const latestSubmission = sortedData.find((att: any) => att.actual && att.actual.trim().length > 0) || sortedData[0];
                    if (latestSubmission && latestSubmission.actual && latestSubmission.actual.trim().length > 0) {
                        setCode(latestSubmission.actual);
                        initialCodeLoadedRef.current = true;
                    }
                }
            }
        } catch (e) {
            console.error("Error fetching caza history:", e);
        } finally {
            setLoadingHistory(false);
        }
    };

    useEffect(() => {
        initialCodeLoadedRef.current = false;
        fetchHistory(true);
    }, [escenario.id, userId]);

    // WebSocket subscription for reactive reload when tests finish running
    // Suppressed during active polling to avoid race conditions
    UseWebSocketSubscription(
        [`/topic/caza/${userId}`],
        async (topic: any, msg: any) => {
            if (!isPollingRef.current) {
                fetchHistory(false);
            }
        }
    );

    const onEditorMount = (editor: any, monaco: any) => {
        editorRef.current = editor;
        monacoRef.current = monaco;
    };

    const onCodeChange = (value?: string) => {
        const idx = activeFileIdxRef.current;
        setFiles(prev => prev.map((f, i) => i === idx ? { ...f, content: value || "" } : f));
    };

    const [requirementAttempted, setRequirementAttempted] = useState<boolean>(false);

    // Requirement tags check in code
    const requiredCheck = React.useMemo(() => {
        let raw = escenario.mustInclude;
        if (typeof raw === 'string') raw = raw.split(',');
        const must = (Array.isArray(raw) ? raw : [])
            .map(token => (typeof token === 'string' ? token.trim() : ''))
            .filter(token => token.length > 0);
        return must.map(token => ({
            token,
            present: code.includes(token)
        }));
    }, [code, escenario.mustInclude]);

    const forbiddenCheck = React.useMemo(() => {
        let raw = escenario.mustNotInclude;
        if (typeof raw === 'string') raw = raw.split(',');
        const mustnot = (Array.isArray(raw) ? raw : [])
            .map(token => (typeof token === 'string' ? token.trim() : ''))
            .filter(token => token.length > 0);
        return mustnot.map(token => ({
            token,
            triggered: code.includes(token)
        }));
    }, [code, escenario.mustNotInclude]);

    const missingRequired = requiredCheck.filter(r => !r.present);
    const triggeredForbidden = forbiddenCheck.filter(f => f.triggered);
    const hasRequirementViolation = missingRequired.length > 0 || triggeredForbidden.length > 0;

    // Reset requirement violation attempt flag when user changes code and fixes requirements
    useEffect(() => {
        if (!hasRequirementViolation && requirementAttempted) {
            setRequirementAttempted(false);
        }
    }, [hasRequirementViolation, requirementAttempted]);

    // Validate current step
    const handleValidarPaso = async () => {
        const isLastStep = pasoActual >= totalSteps;

        // If on the last step and must / must-not are configured, verify them
        if (isLastStep && (requiredCheck.length > 0 || forbiddenCheck.length > 0)) {
            if (hasRequirementViolation) {
                setRequirementAttempted(true);
                const errorParts: string[] = [];
                if (missingRequired.length > 0) {
                    errorParts.push(`Debes incluir en tu código: ${missingRequired.map(m => `"${m.token}"`).join(", ")}.`);
                }
                if (triggeredForbidden.length > 0) {
                    errorParts.push(`No debes utilizar: ${triggeredForbidden.map(f => `"${f.token}"`).join(", ")}.`);
                }

                Modal.error({
                    title: "Requisitos de código obligatorios no cumplidos",
                    content: (
                        <div>
                            <p style={{ marginBottom: 8, fontWeight: 500 }}>
                                Para completar el paso final debes cumplir todas las restricciones de código:
                            </p>
                            <ul style={{ paddingLeft: 20, color: "#cf1322", fontSize: 13, marginBottom: 8 }}>
                                {errorParts.map((msg, idx) => (
                                    <li key={idx}>{msg}</li>
                                ))}
                            </ul>
                            <p style={{ fontSize: 12, color: "#888" }}>
                                Ajusta tu código en el editor para que se reactive el botón de validación.
                            </p>
                        </div>
                    )
                });
                return;
            }
        }

        setValidatingStep(true);
        isPollingRef.current = true; // Block WebSocket refreshes during polling
        const attempts = escenario.intentos_restantes !== undefined ? escenario.intentos_restantes : 0;
        const currentEvaluatingStep = pasoActual;

        try {
            const res = await PostCazaResposta(escenario.id.toString(), userId, code, attempts, currentEvaluatingStep, conjuntId);
            if (!res || (res.response !== "OK" && res.status !== "ok")) {
                isPollingRef.current = false;
                setValidatingStep(false);
                Modal.error({
                    title: `Error al enviar código`,
                    content: res?.message || "No se pudo iniciar la validación de la prueba."
                });
                return;
            }

            // The backend runs tests asynchronously and returns HTTP 200 { response: "OK" }.
            // Capture the highest known attempt ID BEFORE we submitted, so we only look at the new one.
            const previousTopId = history && history.length > 0 
                ? Math.max(...history.map((h: any) => Number(h.id) || 0)) 
                : 0;

            let maxPolls = 25; // up to ~25 seconds
            let finishedAttempt: any = null;

            while (maxPolls > 0) {
                await new Promise((r) => setTimeout(r, 1000));
                try {
                    const data = await GetCazaHistoryAdmin(escenario.id.toString(), userId, conjuntId);
                    if (Array.isArray(data) && data.length > 0) {
                        // Sort by ID descending to always examine the newest attempts first
                        const sorted = [...data].sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0));

                        // Find the newest attempt created AFTER previousTopId that has finished processing (estat !== -1)
                        const candidate = sorted.find((att: any) =>
                            (Number(att.id) || 0) > previousTopId &&
                            att.estat !== -1
                        );
                        if (candidate) {
                            finishedAttempt = candidate;
                            break;
                        }
                    }
                } catch (e) {
                    console.error("Polling error:", e);
                }
                maxPolls--;
            }

            // Always refresh history so the new attempt (correct or incorrect) is immediately listed
            await fetchHistory(false, false);

            isPollingRef.current = false;
            setValidatingStep(false);

            if (!finishedAttempt) {
                Modal.info({
                    title: "Validación en segundo plano",
                    content: "Las pruebas siguen ejecutándose. Revisa la pestaña 'Historial de Envíos' en unos instantes para ver el resultado."
                });
                return;
            }

            const confirmedStep = currentEvaluatingStep;

            // An attempt is a success IF AND ONLY IF estat === 1 (0 = failed, -1 = pending)
            const isSuccess = finishedAttempt.estat === 1;

            if (isSuccess) {
                const newMax = Math.max(maxPasoCompletado, confirmedStep);
                setMaxPasoCompletado(newMax);

                if (confirmedStep < totalSteps) {
                    Modal.success({
                        title: `¡Paso ${confirmedStep} superado! 🎉`,
                        content: `Has superado los tests del paso ${confirmedStep}. Continúa con el siguiente paso conservando tu código.`,
                        okText: `Avanzar al Paso ${confirmedStep + 1}`,
                        onOk: () => {
                            setPasoActual(confirmedStep + 1);
                        }
                    });
                } else {
                    Modal.success({
                        title: "¡Píldora completada con éxito! 🏆",
                        content: "Has completado todos los pasos de esta píldora formativa.",
                        okText: "Finalizar y Salir",
                        onOk: () => onExit()
                    });
                }
            } else {
                const errorMessage = (finishedAttempt.errors && finishedAttempt.errors.trim() !== "OK")
                    ? finishedAttempt.errors
                    : "El código no cumplió todos los requisitos del paso.";

                Modal.error({
                    title: `No se superó el Paso ${confirmedStep}`,
                    content: (
                        <div>
                            <p style={{ marginBottom: 8, fontWeight: 500 }}>
                                El código enviado no pasó las pruebas del paso actual.
                            </p>
                            <pre style={{
                                background: "#fff1f0",
                                border: "1px solid #ffa39e",
                                padding: 8,
                                borderRadius: 4,
                                fontSize: 12,
                                color: "#cf1322",
                                maxHeight: 180,
                                overflowY: "auto",
                                whiteSpace: "pre-wrap"
                            }}>
                                {errorMessage}
                            </pre>
                            <p style={{ fontSize: 12, color: "#888", marginTop: 8 }}>
                                Revisa los detalles en la pestaña "Historial de Envíos" y ajusta tu código antes de reintentar.
                            </p>
                        </div>
                    )
                });
            }
        } catch (err) {
            console.error(err);
            Modal.error({ title: "Error", content: "Error de comunicación con el servidor al enviar el código." });
        } finally {
            isPollingRef.current = false;
            setValidatingStep(false);
            fetchHistory(false, false);
        }
    };

    return (
        <Modal
            open
            footer={null}
            closable={false}
            keyboard={false}
            maskClosable={false}
            width="95%"
            style={{ top: 15, padding: 0, margin: "0 auto", height: "92vh" }}
            bodyStyle={{
                height: "92vh",
                overflow: "hidden",
                padding: 0,
                display: "flex",
                background: "#f8fafc",
                borderRadius: 16,
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
            }}
            wrapClassName="custom-clean-console-modal"
        >
            <div style={{ display: "flex", width: "100%", height: "100%", overflow: "hidden" }}>
                
                {/* LEFT COLUMN: Clean Learning Panel & Tabs */}
                <div style={{
                    width: "42%",
                    minWidth: 460,
                    maxWidth: 600,
                    background: "#ffffff",
                    borderRight: "1px solid #e2e8f0",
                    display: "flex",
                    flexDirection: "column",
                    height: "100%"
                }}>
                    {/* Header */}
                    <div style={{
                        padding: "16px 20px",
                        borderBottom: "1px solid #e2e8f0",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        background: "#f1f5f9"
                    }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, overflow: "hidden" }}>
                            <Button
                                size="small"
                                icon={<ArrowLeftOutlined />}
                                onClick={onExit}
                                style={{ borderRadius: 6 }}
                            >
                                Salir
                            </Button>
                            <div style={{ overflow: "hidden" }}>
                                <Title level={5} style={{ margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {escenario.nombre}
                                </Title>
                                {escenario.aula && (
                                    <Tag color="blue" style={{ fontSize: 11, marginTop: 2 }}>{escenario.aula}</Tag>
                                )}
                            </div>
                        </div>

                        {theoryUrls.length > 0 && (
                            <Button
                                type="primary"
                                icon={<BookOutlined />}
                                onClick={() => setTheoryDrawerOpen(true)}
                                style={{
                                    background: "#0284c7",
                                    borderColor: "#0284c7",
                                    borderRadius: 6,
                                    fontWeight: 600,
                                    fontSize: 12
                                }}
                            >
                                Teoría
                            </Button>
                        )}
                    </div>

                    {/* Left Column Tabs: Instrucciones & Pasos vs Historial */}
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                        <Tabs
                            activeKey={activeTab}
                            onChange={setActiveTab}
                            tabBarStyle={{ padding: "0 20px", margin: 0, background: "#fafafa", borderBottom: "1px solid #e2e8f0" }}
                            items={[
                                {
                                    key: "instrucciones",
                                    label: (
                                        <span>
                                            <CodeOutlined /> Pasos del Ejercicio (Paso {pasoActual} de {totalSteps})
                                        </span>
                                    ),
                                    children: (
                                        <div style={{ padding: 20, height: "calc(92vh - 130px)", overflowY: "auto" }}>
                                            {/* General statement */}
                                            <Card
                                                size="small"
                                                title={<span style={{ color: "#334155" }}>📋 Enunciado General</span>}
                                                style={{ marginBottom: 16, background: "#f8fafc", borderColor: "#e2e8f0" }}
                                            >
                                                <Text style={{ fontSize: 13, color: "#475569" }}>{escenario.descripcion}</Text>
                                            </Card>

                                            {/* Required & Prohibited Tags (only rendered if non-empty in DB) */}
                                            {(requiredCheck.length > 0 || forbiddenCheck.length > 0) && (
                                                <div style={{
                                                    marginBottom: 16,
                                                    padding: requirementAttempted && hasRequirementViolation ? 12 : 0,
                                                    borderRadius: 8,
                                                    background: requirementAttempted && hasRequirementViolation ? "#fff1f0" : "transparent",
                                                    border: requirementAttempted && hasRequirementViolation ? "1px solid #ff4d4f" : "none",
                                                    transition: "all 0.3s ease"
                                                }}>
                                                    {requirementAttempted && hasRequirementViolation && (
                                                        <div style={{
                                                            color: "#cf1322",
                                                            fontSize: 12,
                                                            fontWeight: 600,
                                                            marginBottom: 8,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 6
                                                        }}>
                                                            <span>⚠️ No puedes finalizar el ejercicio: debes cumplir los requisitos de código requeridos y evitar los prohibidos.</span>
                                                        </div>
                                                    )}
                                                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                                                        {requiredCheck.length > 0 && (
                                                            <div style={{ flex: 1, minWidth: 180, background: "#f0fdf4", padding: "8px 12px", borderRadius: 8, border: "1px solid #bbf7d0" }}>
                                                                <div style={{ fontSize: 11, fontWeight: 700, color: "#166534", marginBottom: 6 }}>
                                                                    ✓ DEBE INCLUIR
                                                                </div>
                                                                <Space wrap size={4}>
                                                                    {requiredCheck.map(r => (
                                                                        <Tag key={r.token} color={r.present ? "green" : (requirementAttempted ? "error" : "default")}>
                                                                            {r.present ? "✓ " : "○ "}{r.token}
                                                                        </Tag>
                                                                    ))}
                                                                </Space>
                                                            </div>
                                                        )}
                                                        {forbiddenCheck.length > 0 && (
                                                            <div style={{ flex: 1, minWidth: 180, background: "#fef2f2", padding: "8px 12px", borderRadius: 8, border: "1px solid #fecaca" }}>
                                                                <div style={{ fontSize: 11, fontWeight: 700, color: "#991b1b", marginBottom: 6 }}>
                                                                    ✕ PROHIBIDO
                                                                </div>
                                                                <Space wrap size={4}>
                                                                    {forbiddenCheck.map(f => (
                                                                        <Tag key={f.token} color={f.triggered ? "red" : "default"}>
                                                                            {f.triggered ? "⚠️ " : ""}{f.token}
                                                                        </Tag>
                                                                    ))}
                                                                </Space>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                        {/* Progressive Steps */}
                                        <div style={{ marginTop: 10 }}>
                                            <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>
                                                Itinerario de Pasos
                                            </div>

                                            {parsedPasos.map((p, idx) => {
                                                const stepNum = p.paso || idx + 1;
                                                const isCompleted = stepNum <= maxPasoCompletado;
                                                const isActive = stepNum === pasoActual;
                                                const isLocked = stepNum > maxPasoCompletado + 1;

                                                if (isLocked) {
                                                    // Hidden/Locked Step
                                                    return (
                                                        <div
                                                            key={stepNum}
                                                            style={{
                                                                padding: "12px 16px",
                                                                marginBottom: 10,
                                                                borderRadius: 8,
                                                                background: "#f1f5f9",
                                                                border: "1px dashed #cbd5e1",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 10,
                                                                color: "#94a3b8"
                                                            }}
                                                        >
                                                            <LockOutlined style={{ fontSize: 16 }} />
                                                            <div>
                                                                <Text disabled strong style={{ fontSize: 13 }}>Paso {stepNum}: Bloqueado</Text>
                                                                <div style={{ fontSize: 11, color: "#94a3b8" }}>
                                                                    Supera el paso {stepNum - 1} para desbloquear este tramo.
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                }

                                                if (isActive) {
                                                    // Current Viewing/Active Step (Fully Visible & Highlighted)
                                                    return (
                                                        <Card
                                                            key={stepNum}
                                                            size="small"
                                                            style={{
                                                                marginBottom: 12,
                                                                borderColor: isCompleted ? "#16a34a" : "#0284c7",
                                                                background: isCompleted ? "#f0fdf4" : "#f0f9ff",
                                                                boxShadow: isCompleted ? "0 4px 12px rgba(22, 163, 74, 0.08)" : "0 4px 12px rgba(2, 132, 199, 0.08)"
                                                            }}
                                                            title={
                                                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                                    <Space>
                                                                        <Tag color={isCompleted ? "#16a34a" : "#0284c7"} style={{ fontWeight: "bold" }}>
                                                                            {isCompleted ? "SUPERADO" : "ACTUAL"}
                                                                        </Tag>
                                                                        <Text strong style={{ color: isCompleted ? "#166534" : "#0369a1", fontSize: 14 }}>{p.titulo}</Text>
                                                                    </Space>
                                                                    <Tag color={isCompleted ? "green" : "cyan"}>Paso {stepNum}</Tag>
                                                                </div>
                                                            }
                                                        >
                                                            <div style={{
                                                                fontSize: 13,
                                                                lineHeight: 1.6,
                                                                color: "#0f172a",
                                                                whiteSpace: "pre-wrap",
                                                                background: "#ffffff",
                                                                padding: 12,
                                                                borderRadius: 6,
                                                                border: isCompleted ? "1px solid #bbf7d0" : "1px solid #e0f2fe"
                                                            }}>
                                                                {p.instrucciones}
                                                            </div>
                                                        </Card>
                                                    );
                                                }

                                                // Completed Step (Collapsed/Green)
                                                if (isCompleted) {
                                                    return (
                                                        <div
                                                            key={stepNum}
                                                            onClick={() => setPasoActual(stepNum)}
                                                            style={{
                                                                padding: "10px 14px",
                                                                marginBottom: 10,
                                                                borderRadius: 8,
                                                                background: "#f0fdf4",
                                                                border: "1px solid #bbf7d0",
                                                                cursor: "pointer",
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                                alignItems: "center"
                                                            }}
                                                        >
                                                            <Space>
                                                                <CheckCircleOutlined style={{ color: "#16a34a", fontSize: 16 }} />
                                                                <Text strong style={{ color: "#166534", fontSize: 13 }}>{p.titulo}</Text>
                                                            </Space>
                                                            <Tag color="green">Superado (Clic para ver)</Tag>
                                                        </div>
                                                    );
                                                }

                                                // Unlocked but not active and not completed (e.g. user navigated back to previous step)
                                                return (
                                                    <div
                                                        key={stepNum}
                                                        onClick={() => setPasoActual(stepNum)}
                                                        style={{
                                                            padding: "10px 14px",
                                                            marginBottom: 10,
                                                            borderRadius: 8,
                                                            background: "#f8fafc",
                                                            border: "1px solid #e2e8f0",
                                                            cursor: "pointer",
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            alignItems: "center"
                                                        }}
                                                    >
                                                        <Space>
                                                            <Text strong style={{ color: "#334155", fontSize: 13 }}>{p.titulo}</Text>
                                                        </Space>
                                                        <Tag color="blue">Paso {stepNum} (Pendiente)</Tag>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ),
                            },
                            {
                                key: "historial",
                                label: (
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                        <HistoryOutlined /> Historial de Envíos ({history.length})
                                    </span>
                                ),
                                children: (
                                    <div style={{ padding: 20, height: "calc(92vh - 130px)", overflowY: "auto" }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                Envíos registrados ({history.length})
                                            </Text>
                                            <Button
                                                size="small"
                                                icon={<ReloadOutlined spin={loadingHistory} />}
                                                loading={loadingHistory}
                                                onClick={() => fetchHistory(false, false)}
                                            >
                                                Actualizar Historial
                                            </Button>
                                        </div>
                                        {loadingHistory && history.length === 0 ? (
                                            <div style={{ textAlign: "center", marginTop: 40 }}>Cargando historial...</div>
                                        ) : history.length === 0 ? (
                                            <div style={{ textAlign: "center", color: "#94a3b8", marginTop: 40 }}>
                                                Aún no has realizado ningún envío en esta píldora.
                                            </div>
                                        ) : (
                                            <Collapse accordion defaultActiveKey={[history[0]?.id]}>
                                                {history.map((att) => {
                                                    const isOk = att.estat === 1;
                                                    const isEvaluating = att.estat === -1;
                                                    return (
                                                        <Panel
                                                            key={att.id}
                                                            header={
                                                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                                                    <Space>
                                                                        <Tag color="purple">Paso {att.ordre || 1}</Tag>
                                                                        <Tag color={isOk ? "green" : isEvaluating ? "orange" : "red"}>
                                                                            {isOk ? "CORRECTO" : isEvaluating ? "EVALUANDO" : "INCORRECTO"}
                                                                        </Tag>
                                                                        <span style={{ fontSize: 11, color: "#64748b" }}>{att.moment}</span>
                                                                    </Space>
                                                                </div>
                                                            }
                                                        >
                                                            <div style={{ marginBottom: 10 }}>
                                                                <div style={{ fontSize: 12, fontWeight: 600, color: "#334155", marginBottom: 4 }}>
                                                                    Salida / Error:
                                                                </div>
                                                                <pre style={{
                                                                    background: isOk ? "#f0fdf4" : "#fef2f2",
                                                                    border: isOk ? "1px solid #bbf7d0" : "1px solid #fecaca",
                                                                    padding: 10,
                                                                    borderRadius: 4,
                                                                    fontSize: 12,
                                                                    color: isOk ? "#166534" : "#991b1b",
                                                                    maxHeight: 140,
                                                                    overflowY: "auto",
                                                                    whiteSpace: "pre-wrap"
                                                                }}>
                                                                    {att.errors && att.errors.trim().length > 0 && (!att.errors.trim().startsWith("OK") || isOk)
                                                                        ? att.errors
                                                                        : (isOk
                                                                            ? "Todos los tests superados con éxito"
                                                                            : "El código no superó las pruebas de validación.")}
                                                                </pre>
                                                            </div>
                                                            <div>
                                                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                                                                    <span style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>Código enviado:</span>
                                                                    <Button
                                                                        size="small"
                                                                        onClick={() => setCode(att.actual)}
                                                                    >
                                                                        Cargar en editor
                                                                    </Button>
                                                                </div>
                                                                <div style={{ height: 200, border: "1px solid #e2e8f0", borderRadius: 4, overflow: "hidden" }}>
                                                                    <Editor
                                                                        height="100%"
                                                                        language="java"
                                                                        value={att.actual || "// No code"}
                                                                        options={{ readOnly: true, minimap: { enabled: false } }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </Panel>
                                                    );
                                                })}
                                            </Collapse>
                                        )}
                                    </div>
                                )
                            }
                        ]}
                    />
                </div>
            </div>

            {/* RIGHT COLUMN: Professional Monaco Code Editor & Action Bar */}
            <div style={{ flex: 1, display: "flex", flexDirection: "column", height: "100%", background: "#ffffff" }}>
                
                {/* Editor Tab Bar */}
                <div style={{
                    padding: "10px 20px",
                    borderBottom: "1px solid #e2e8f0",
                    background: "#f8fafc",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}>
                    <Space>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Lenguaje: Java 17
                        </Text>
                        {files.length > 1 && (
                            <Tag color="purple">{files.length} ficheros (se enviarán todos)</Tag>
                        )}
                    </Space>

                    <Space>
                        <Tag color={pasoActual <= maxPasoCompletado ? "green" : "gold"}>
                            {pasoActual <= maxPasoCompletado ? `Paso ${pasoActual} Superado` : `Validando Paso ${pasoActual}`}
                        </Tag>
                        {escenario.intentos_global !== 0 && escenario.intentos_global !== null && escenario.intentos_restantes !== undefined && (
                            <Tag color="cyan">
                                Intentos restantes: {escenario.intentos_restantes}
                            </Tag>
                        )}
                    </Space>
                </div>

                {/* File tabs (multi-fichero) */}
                <div style={{ padding: "6px 12px 0 12px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                    <Tabs
                        type="editable-card"
                        size="small"
                        activeKey={String(activeFileIdx)}
                        onChange={(key) => setActiveFileIdx(Number(key))}
                        onEdit={(targetKey, action) => {
                            if (action === "add") openAddFile();
                            else removeFile(Number(targetKey));
                        }}
                        addIcon={
                            <Tooltip title="Nuevo fichero">
                                <FileAddOutlined />
                            </Tooltip>
                        }
                        tabBarStyle={{ margin: 0 }}
                        items={files.map((f, idx) => ({
                            key: String(idx),
                            closable: files.length > 1,
                            label: (
                                <Tooltip title="Doble clic para renombrar">
                                    <span onDoubleClick={() => openRenameFile(idx)} style={{ fontSize: 12 }}>
                                        {f.name}
                                    </span>
                                </Tooltip>
                            )
                        }))}
                    />
                </div>

                {/* Monaco Editor Container */}
                <div style={{ flex: 1, position: "relative" }}>
                    <Editor
                        height="100%"
                        language="java"
                        theme="light"
                        path={`caza-${escenario.id}/${activeFile.name}`}
                        value={activeFile.content}
                        onChange={onCodeChange}
                        onMount={onEditorMount}
                        options={{
                            fontSize: 14,
                            minimap: { enabled: false },
                            lineNumbers: "on",
                            padding: { top: 16, bottom: 16 },
                            renderLineHighlight: "line",
                            automaticLayout: true
                        }}
                    />
                </div>

                {/* Modal crear / renombrar fichero */}
                <Modal
                    open={!!fileNameModal}
                    title={fileNameModal?.mode === "rename" ? "Renombrar fichero" : "Nuevo fichero Java"}
                    okText={fileNameModal?.mode === "rename" ? "Renombrar" : "Crear"}
                    cancelText="Cancelar"
                    onOk={confirmFileName}
                    onCancel={() => setFileNameModal(null)}
                    destroyOnClose
                >
                    <Input
                        autoFocus
                        placeholder="Persona.java"
                        value={fileNameInput}
                        onChange={(e) => { setFileNameInput(e.target.value); setFileNameError(""); }}
                        onPressEnter={confirmFileName}
                        status={fileNameError ? "error" : undefined}
                    />
                    {fileNameError ? (
                        <div style={{ color: "#cf1322", fontSize: 12, marginTop: 6 }}>{fileNameError}</div>
                    ) : (
                        <div style={{ color: "#64748b", fontSize: 12, marginTop: 6 }}>
                            El nombre debe coincidir con la clase pública del fichero. La clase con el método <code>main</code> será la que se ejecute.
                        </div>
                    )}
                </Modal>

                {/* Bottom Action Bar */}
                <div style={{
                    height: 64,
                    borderTop: "1px solid #e2e8f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0 24px",
                    background: "#f8fafc"
                }}>
                    <Space>
                        {pasoActual > 1 && (
                            <Button
                                onClick={() => setPasoActual(prev => Math.max(1, prev - 1))}
                            >
                                ← Paso {pasoActual - 1}
                            </Button>
                        )}
                        {pasoActual < totalSteps && pasoActual <= maxPasoCompletado && (
                            <Button
                                onClick={() => setPasoActual(prev => Math.min(totalSteps, prev + 1))}
                            >
                                Paso {pasoActual + 1} →
                            </Button>
                        )}
                    </Space>

                    {pasoActual <= maxPasoCompletado ? (
                        <Button
                            type="default"
                            icon={<CheckCircleOutlined style={{ color: "#16a34a" }} />}
                            onClick={() => {
                                if (pasoActual < totalSteps) {
                                    setPasoActual(pasoActual + 1);
                                }
                            }}
                            style={{
                                borderColor: "#bbf7d0",
                                background: "#f0fdf4",
                                color: "#166534",
                                padding: "0 20px",
                                height: 42,
                                fontSize: 13,
                                fontWeight: 600,
                                borderRadius: 6
                            }}
                        >
                            {pasoActual < totalSteps ? `Paso ${pasoActual} Superado (Ir al Paso ${pasoActual + 1} →)` : "Paso Final Superado"}
                        </Button>
                    ) : (pasoActual >= totalSteps && requirementAttempted && hasRequirementViolation) ? (
                        <Button
                            danger
                            style={{
                                height: 42,
                                fontSize: 13,
                                fontWeight: 600,
                                borderRadius: 6,
                                background: "#fff1f0",
                                borderColor: "#ffa39e",
                                color: "#cf1322"
                            }}
                            onClick={() => {
                                setActiveTab("instrucciones");
                                Modal.warning({
                                    title: "Requisitos de código pendientes",
                                    content: "Cumple las directivas de 'DEBE INCLUIR' y elimina las de 'PROHIBIDO' en tu código para poder finalizar la píldora."
                                });
                            }}
                        >
                            ⚠️ Requisitos de código incompletos
                        </Button>
                    ) : (
                        <Button
                            type="primary"
                            icon={<SendOutlined />}
                            loading={validatingStep}
                            onClick={handleValidarPaso}
                            style={{
                                background: "linear-gradient(135deg, #0284c7, #2563eb)",
                                borderColor: "#0284c7",
                                color: "#fff",
                                padding: "0 24px",
                                height: 42,
                                fontSize: 14,
                                fontWeight: 600,
                                borderRadius: 6
                            }}
                        >
                            {pasoActual < totalSteps ? `Validar Paso ${pasoActual} de ${totalSteps}` : "Validar y Finalizar Píldora"}
                        </Button>
                    )}
                </div>
            </div>

        </div>

        {/* GITLAB PAGES THEORY DRAWER */}
        <Drawer
            title={
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", paddingRight: 24 }}>
                    <span style={{ fontSize: 16, fontWeight: "bold" }}>📖 Guía Teórica y Documentación</span>
                    <Button
                        size="small"
                        type="primary"
                        icon={<ExportOutlined />}
                        onClick={() => window.open(activeTheoryUrl, '_blank')}
                    >
                        Abrir en nueva pestaña
                    </Button>
                </div>
            }
            placement="right"
            width="65%"
            onClose={() => setTheoryDrawerOpen(false)}
            open={theoryDrawerOpen}
            bodyStyle={{ padding: 0, height: "100%", overflow: "hidden" }}
        >
            <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
                {/* If multiple URLs exist, show tabs at the top */}
                {theoryUrls.length > 1 && (
                    <div style={{ padding: "8px 16px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                        <Tabs
                            size="small"
                            activeKey={String(selectedTheoryIdx)}
                            onChange={(key) => setSelectedTheoryIdx(Number(key))}
                            items={theoryUrls.map((u, idx) => ({
                                key: String(idx),
                                label: `Documento ${idx + 1}`
                            }))}
                        />
                    </div>
                )}
                <div style={{
                    padding: "8px 16px",
                    background: "#f1f5f9",
                    borderBottom: "1px solid #e2e8f0",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 12,
                    color: "#64748b"
                }}>
                    <span>Enlace activo:</span>
                    <a href={activeTheoryUrl} target="_blank" rel="noreferrer" style={{ color: "#0284c7", wordBreak: "break-all" }}>
                        {activeTheoryUrl}
                    </a>
                </div>
                <iframe
                    key={activeTheoryUrl}
                    src={activeTheoryUrl}
                    title="Teoria GitLab Pages"
                    style={{
                        width: "100%",
                        flex: 1,
                        border: "none"
                    }}
                />
            </div>
        </Drawer>
    </Modal>
    );
};

export default CazaModal;
